import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ChevronLeft, Plus, Pencil, Trash2, Loader2, Save, MessageSquareText, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminChatSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [cooldown, setCooldown] = useState(10);
  const [cooldownInput, setCooldownInput] = useState("10");
  const [savingCooldown, setSavingCooldown] = useState(false);

  // template dialog
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditId, setTplEditId] = useState(null);
  const [tplName, setTplName] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  // rule dialog
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleEditId, setRuleEditId] = useState(null);
  const [ruleTrigger, setRuleTrigger] = useState("");
  const [ruleTemplateId, setRuleTemplateId] = useState("");
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [savingRule, setSavingRule] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [tplRes, ruleRes, setRes] = await Promise.all([
        axios.get(`${API}/admin/chat-templates`),
        axios.get(`${API}/admin/chat-auto-replies`),
        axios.get(`${API}/admin/app-settings/chat-templates`),
      ]);
      setTemplates(tplRes.data || []);
      setRules(ruleRes.data || []);
      setAutoReplyEnabled(!!setRes.data?.auto_reply_enabled);
      const cd = setRes.data?.auto_reply_cooldown_min ?? 10;
      setCooldown(cd);
      setCooldownInput(String(cd));
    } catch (e) {
      toast.error("Failed to load chat settings.");
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ----- templates -----
  const openNewTpl = () => { setTplEditId(null); setTplName(""); setTplBody(""); setTplOpen(true); };
  const openEditTpl = (t) => { setTplEditId(t.id); setTplName(t.name); setTplBody(t.body); setTplOpen(true); };
  const saveTpl = async () => {
    if (!tplName.trim() || !tplBody.trim()) { toast.error("Name and message are required."); return; }
    setSavingTpl(true);
    try {
      if (tplEditId) {
        await axios.put(`${API}/admin/chat-templates/${tplEditId}`, { name: tplName.trim(), body: tplBody });
      } else {
        await axios.post(`${API}/admin/chat-templates`, { name: tplName.trim(), body: tplBody });
      }
      setTplOpen(false);
      await fetchAll();
      toast.success("Template saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template.");
    } finally { setSavingTpl(false); }
  };
  const deleteTpl = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Any auto-reply rules using it are removed too.`)) return;
    try {
      await axios.delete(`${API}/admin/chat-templates/${t.id}`);
      await fetchAll();
      toast.success("Template deleted.");
    } catch (e) { toast.error("Could not delete template."); }
  };

  // ----- rules -----
  const openNewRule = () => {
    if (templates.length === 0) { toast.error("Create a template first."); return; }
    setRuleEditId(null); setRuleTrigger(""); setRuleTemplateId(templates[0].id); setRuleEnabled(true); setRuleOpen(true);
  };
  const openEditRule = (r) => {
    setRuleEditId(r.id); setRuleTrigger(r.trigger); setRuleTemplateId(r.template_id); setRuleEnabled(r.enabled); setRuleOpen(true);
  };
  const saveRule = async () => {
    if (!ruleTrigger.trim() || !ruleTemplateId) { toast.error("Trigger and template are required."); return; }
    setSavingRule(true);
    try {
      const payload = { trigger: ruleTrigger.trim(), template_id: ruleTemplateId, enabled: ruleEnabled };
      if (ruleEditId) await axios.put(`${API}/admin/chat-auto-replies/${ruleEditId}`, payload);
      else await axios.post(`${API}/admin/chat-auto-replies`, payload);
      setRuleOpen(false);
      await fetchAll();
      toast.success("Rule saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save rule.");
    } finally { setSavingRule(false); }
  };
  const deleteRule = async (r) => {
    if (!window.confirm("Delete this auto-reply rule?")) return;
    try {
      await axios.delete(`${API}/admin/chat-auto-replies/${r.id}`);
      await fetchAll();
      toast.success("Rule deleted.");
    } catch (e) { toast.error("Could not delete rule."); }
  };
  const toggleRule = async (r) => {
    try {
      await axios.put(`${API}/admin/chat-auto-replies/${r.id}`, { enabled: !r.enabled });
      setRules((rs) => rs.map((x) => (x.id === r.id ? { ...x, enabled: !x.enabled } : x)));
    } catch (e) { toast.error("Could not update rule."); }
  };

  // ----- auto-reply settings -----
  const toggleAutoReply = async () => {
    const next = !autoReplyEnabled;
    setAutoReplyEnabled(next);
    try {
      await axios.put(`${API}/admin/app-settings/chat-templates`, { auto_reply_enabled: next });
    } catch (e) { toast.error("Could not update."); setAutoReplyEnabled(!next); }
  };
  const saveCooldown = async () => {
    const n = parseInt(cooldownInput, 10);
    if (Number.isNaN(n) || n < 0) { toast.error("Enter a number of minutes (0 or more)."); return; }
    setSavingCooldown(true);
    try {
      await axios.put(`${API}/admin/app-settings/chat-templates`, { auto_reply_cooldown_min: n });
      setCooldown(n);
      toast.success("Cooldown saved.");
    } catch (e) { toast.error("Could not save cooldown."); } finally { setSavingCooldown(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <Link to="/admin/chat" title="Back to Support Chat" className="text-white/70 hover:text-[#f4cc1e]"><ChevronLeft size={22} /></Link>
        <h1 className="text-2xl font-bold text-white font-['Chivo']">Chat Templates &amp; Auto-Reply</h1>
      </div>

      {/* Templates */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a] flex flex-row items-center justify-between">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <MessageSquareText size={16} className="text-[#f4cc1e]" /> Templates
          </CardTitle>
          <Button size="sm" onClick={openNewTpl} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
            <Plus size={15} className="mr-1" /> New Template
          </Button>
        </CardHeader>
        <CardContent className="px-5 py-2">
          <p className="text-xs text-white/50 py-2">Reusable replies. Use the Templates button in a conversation to drop one into the compose box. Use {"{{name}}"} and {"{{company}}"} to personalize.</p>
          {templates.length === 0 ? (
            <div className="py-6 text-center text-white/40 text-sm">No templates yet.</div>
          ) : templates.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 py-3 border-b border-[#1a3a6a] last:border-b-0">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">{t.name}</p>
                <p className="text-xs text-white/50 truncate max-w-[520px]">{t.body}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEditTpl(t)} title="Edit" className="text-white/50 hover:text-[#f4cc1e] p-1"><Pencil size={15} /></button>
                <button onClick={() => deleteTpl(t)} title="Delete" className="text-white/40 hover:text-red-400 p-1"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Auto-reply */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <Zap size={16} className="text-[#f4cc1e]" /> Auto-Reply
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">When a customer's chat message <span className="text-white/70">contains</span> a trigger phrase, we reply with its template. One auto-reply per conversation within the cooldown window.</p>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-white">Enable auto-reply</p>
              <p className="text-xs text-white/50">Master switch for all rules below</p>
            </div>
            <button type="button" onClick={toggleAutoReply}
              className={`h-6 w-11 rounded-full transition-colors relative ${autoReplyEnabled ? "bg-[#f4cc1e]" : "bg-[#1a3a6a]"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${autoReplyEnabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-[#1a3a6a] pt-4">
            <div>
              <p className="text-sm font-medium text-white">Cooldown (minutes)</p>
              <p className="text-xs text-white/50">Minimum gap between auto-replies on one conversation</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" value={cooldownInput} onChange={(e) => setCooldownInput(e.target.value)}
                className="w-24 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white" />
              <Button size="sm" onClick={saveCooldown} disabled={savingCooldown || cooldownInput === String(cooldown)}
                className="h-9 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90">
                {savingCooldown ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </Button>
            </div>
          </div>

          <div className="border-t border-[#1a3a6a] pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-white">Rules</p>
              <Button size="sm" variant="outline" onClick={openNewRule} className="h-8 border-[#1a3a6a] text-white hover:bg-[#0d2a5a]">
                <Plus size={14} className="mr-1" /> New Rule
              </Button>
            </div>
            {rules.length === 0 ? (
              <div className="py-6 text-center text-white/40 text-sm">No rules yet.</div>
            ) : rules.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-3 border-b border-[#1a3a6a] last:border-b-0">
                <div className="min-w-0">
                  <p className="text-sm text-white">Contains <span className="font-semibold text-[#f4cc1e]">“{r.trigger}”</span> → {r.template_name || <span className="text-red-400">(missing template)</span>}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={() => toggleRule(r)} title={r.enabled ? "Enabled" : "Disabled"}
                    className={`h-5 w-9 rounded-full transition-colors relative ${r.enabled ? "bg-[#f4cc1e]" : "bg-[#1a3a6a]"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${r.enabled ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                  <button onClick={() => openEditRule(r)} title="Edit" className="text-white/50 hover:text-[#f4cc1e] p-1"><Pencil size={15} /></button>
                  <button onClick={() => deleteRule(r)} title="Delete" className="text-white/40 hover:text-red-400 p-1"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Template dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">{tplEditId ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-white/80">Name</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Business Hours"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Message</Label>
              <Textarea value={tplBody} onChange={(e) => setTplBody(e.target.value)} rows={4}
                className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white" />
              <p className="text-xs text-white/40">Use {"{{name}}"} and {"{{company}}"} to personalize.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTplOpen(false)} className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]">Cancel</Button>
            <Button onClick={saveTpl} disabled={savingTpl} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
              {savingTpl ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule dialog */}
      <Dialog open={ruleOpen} onOpenChange={setRuleOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">{ruleEditId ? "Edit Rule" : "New Auto-Reply Rule"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-white/80">Trigger phrase (matches if the message contains it)</Label>
              <Input value={ruleTrigger} onChange={(e) => setRuleTrigger(e.target.value)} placeholder="e.g. hours"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Reply with template</Label>
              <Select value={ruleTemplateId} onValueChange={setRuleTemplateId}>
                <SelectTrigger className="bg-[#0d2a5a] border-[#1a3a6a] text-white"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white/80">Enabled</Label>
              <button type="button" onClick={() => setRuleEnabled((v) => !v)}
                className={`h-6 w-11 rounded-full transition-colors relative ${ruleEnabled ? "bg-[#f4cc1e]" : "bg-[#1a3a6a]"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${ruleEnabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRuleOpen(false)} className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]">Cancel</Button>
            <Button onClick={saveRule} disabled={savingRule} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
              {savingRule ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChatSettings;
